import { Descriptions } from "@/components/v2";
import { Card } from "@rumbledirect/ui";
import { Col, Flex, Row, Tag, type DescriptionsProps } from "antd";
import { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";

const yesNo = (value: boolean | null | undefined) =>
  value === true ? "Ja" : value === false ? "Nee" : null;

export const ClaimDossierRecourseTab = ({
  data,
}: {
  data: Dossier | undefined;
}) => {
  const recourse = data?.recourse;

  if (!recourse) {
    return null;
  }

  const generalItems: DescriptionsProps["items"] = [
    {
      label: "Kenteken",
      children: recourse.licensePlate?.identifier,
    },
    {
      label: "Land kenteken",
      children: recourse.licensePlate?.country,
    },
    {
      label: "Bestuurder",
      children: recourse.driverFullName,
    },
    {
      label: "Schadecontext",
      children: recourse.damageContext?.label || recourse.damageContext?.name,
    },
    {
      label: "Letsel",
      children: yesNo(recourse.injuries),
    },
    {
      label: "Alcohol betrokken",
      children: yesNo(recourse.alcoholInvolved),
    },
    {
      label: "Status",
      children: <Tag color="green">Ingevuld door bestuurder</Tag>,
    },
  ];

  const otherPartyItems: DescriptionsProps["items"] = !recourse.otherParty
    ? []
    : [
        {
          label: "Kenteken tegenpartij",
          children: recourse.otherParty.licensePlate?.identifier,
        },
        {
          label: "Land kenteken tegenpartij",
          children: recourse.otherParty.licensePlate?.country,
        },
        {
          label: "Naam tegenpartij",
          children: recourse.otherParty.person?.fullName,
        },
        {
          label: "E-mail tegenpartij",
          children: recourse.otherParty.person?.email,
        },
        {
          label: "Telefoon tegenpartij",
          children: recourse.otherParty.person?.telephone,
        },
        {
          label: "Adres tegenpartij",
          children:
            [
              recourse.otherParty.address?.street,
              recourse.otherParty.address?.streetNumber,
              recourse.otherParty.address?.streetNumberAddition,
            ]
              .filter(Boolean)
              .join(" ") +
              (recourse.otherParty.address
                ? `, ${recourse.otherParty.address.postalCode} ${recourse.otherParty.address.city}`
                : "") || null,
        },
      ];

  const witnessItems: DescriptionsProps["items"] = !recourse.witness
    ? []
    : [
        {
          label: "Naam getuige",
          children: recourse.witness.person?.fullName,
        },
        {
          label: "E-mail getuige",
          children: recourse.witness.person?.email,
        },
        {
          label: "Telefoon getuige",
          children: recourse.witness.person?.telephone,
        },
        {
          label: "Adres getuige",
          children:
            [
              recourse.witness.address?.street,
              recourse.witness.address?.streetNumber,
              recourse.witness.address?.streetNumberAddition,
            ]
              .filter(Boolean)
              .join(" ") +
              (recourse.witness.address
                ? `, ${recourse.witness.address.postalCode} ${recourse.witness.address.city}`
                : "") || null,
        },
      ];

  const documentsItems: DescriptionsProps["items"] =
    ([
      recourse.policeReport
        ? {
            label: "Proces-verbaal",
            children: (
              <a
                href={recourse.policeReport.contentUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Downloaden
              </a>
            ),
          }
        : null,
      recourse.accidentReport
        ? {
            label: "Schadeformulier",
            children: (
              <a
                href={recourse.accidentReport.contentUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Downloaden
              </a>
            ),
          }
        : null,
    ].filter(Boolean) as DescriptionsProps["items"]) || [];

  return (
    <Flex gap={20} className="w-full !px-0" align="start">
      <Col flex="auto" className="!px-0">
        <Row gutter={[16, 16]}>
          <Col xxl={{ span: 12 }} span={24}>
            <Card className="w-full" title="Algemene regresinformatie">
              <Descriptions items={generalItems} />
            </Card>

            {documentsItems.length > 0 && (
              <Card className="w-full mt-4" title="Documenten">
                <Descriptions items={documentsItems} />
              </Card>
            )}
          </Col>

          <Col xxl={{ span: 12 }} span={24}>
            {otherPartyItems.length > 0 && (
              <Card className="w-full mb-4" title="Tegenpartij">
                <Descriptions items={otherPartyItems} />
              </Card>
            )}
            {witnessItems.length > 0 && (
              <Card className="w-full" title="Getuige">
                <Descriptions items={witnessItems} />
              </Card>
            )}
          </Col>
        </Row>
      </Col>
    </Flex>
  );
};
